import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

interface Album {
  id: number;
  title: string;
  description: string;
  cover_image: string;
}

interface Photo {
  id: number;
  image_url: string; // CORRIGIDO: era "url", mas a coluna no banco se chama "image_url"
  caption: string;
}

export default function AlbumPage() {
  const { albumId } = useParams<{ albumId: string }>();
  const navigate = useNavigate();
  const [album, setAlbum] = useState<Album | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAlbumData() {
      if (!albumId) return;
      setLoading(true);

      const { data: albumData, error: albumError } = await supabase
        .from('portfolio_albums')
        .select('*')
        .eq('id', albumId)
        .maybeSingle();

      if (albumError) {
        console.error('[AlbumPage] Erro ao buscar álbum:', albumError.message);
      }

      if (albumData) {
        setAlbum(albumData);
      }

      const { data: photoData, error: photoError } = await supabase
        .from('portfolio_photos')
        .select('id, image_url, caption, sort_order') // seleção explícita para evitar ambiguidade
        .eq('album_id', albumId)
        .order('sort_order');

      if (photoError) {
        console.error('[AlbumPage] Erro ao buscar fotos:', photoError.message);
      }

      if (photoData) {
        setPhotos(photoData);
      }

      setLoading(false);
    }
    fetchAlbumData();
  }, [albumId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-2 border-dark/20 border-t-dark rounded-full animate-spin" />
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen">
        <SEOHead
          title="Álbum não encontrado | Jéssica Bonini Fotógrafa"
          description="O álbum solicitado não foi encontrado. Explore nosso portfólio completo de fotografia profissional."
          canonicalPath={`/portfolio/${albumId}`}
          noindex
        />
        <Navbar />
        <main className="pt-32 md:pt-40 pb-16 md:pb-24 bg-cream">
          <div className="px-6 md:px-10 lg:px-16 text-center">
            <h1 className="font-serif text-3xl md:text-4xl text-dark mb-4">Álbum não encontrado</h1>
            <button
              onClick={() => navigate('/portfolio')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-dark text-white rounded-full text-sm font-medium transition-colors hover:bg-dark/90"
            >
              <i className="ri-arrow-left-line" />
              Voltar ao Portfólio
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const schema = [
    {
      '@type': 'ImageGallery',
      name: album.title,
      description: album.description,
      url: `${SITE_URL}/portfolio/${album.id}`,
      inLanguage: 'pt-BR',
    },
    {
      '@type': 'WebPage',
      name: album.title,
      description: album.description,
      url: `${SITE_URL}/portfolio/${album.id}`,
    },
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title={`${album.title} | Portfólio Jéssica Bonini Fotógrafa`}
        description={album.description}
        canonicalPath={`/portfolio/${album.id}`}
        ogType="website"
        schema={schema}
      />
      <Navbar />
      <main>
        <section className="pt-32 md:pt-40 pb-8 md:pb-12 bg-cream">
          <div className="px-6 md:px-10 lg:px-16">
            <button
              onClick={() => navigate('/portfolio')}
              className="inline-flex items-center gap-2 text-dark/60 hover:text-dark text-sm font-medium mb-6 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              Voltar ao Portfólio
            </button>
            <div className="max-w-3xl">
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-dark font-normal leading-tight mb-4">
                {album.title}
              </h1>
              <p className="text-dark/60 text-base md:text-lg leading-relaxed">
                {album.description}
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-white">
          <div className="px-6 md:px-10 lg:px-16">
            {photos.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-warm-100 mx-auto mb-4">
                  <i className="ri-image-line text-2xl text-dark/40" />
                </div>
                <p className="text-dark/50 text-lg">Este álbum ainda não possui fotos.</p>
                <p className="text-dark/40 text-sm mt-2">Fotos serão adicionadas em breve.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {photos.map((photo) => (
                  <div
                    key={photo.id}
                    onClick={() => setSelectedPhoto(photo.image_url)} // CORRIGIDO: era photo.url
                    className="group relative aspect-[3/4] rounded-xl md:rounded-2xl overflow-hidden cursor-pointer bg-warm-100"
                  >
                    <img
                      src={photo.image_url} // CORRIGIDO: era photo.url
                      alt={photo.caption || 'Foto do álbum'}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-dark">
                        <i className="ri-fullscreen-line text-lg" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      {selectedPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            onClick={() => setSelectedPhoto(null)}
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={selectedPhoto}
            alt="Visualização em tela cheia"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}